import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Image, Text } from 'rebass/styled-components';
import { Button } from '@getro/rombo';
import { NavLink, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AddCompanyProcess } from 'components/atoms/AddCompanyProcess';
import { companyDetailsSelector } from 'hooks/useCompany/selectors';
import { useLocalStorage } from 'hooks/useLocalStorage';
import PageLayout from '../Page';
import { activeNetworkSelector } from '../../../redux/selectors';
import { Title } from './title';
import Refreshing from '../../../assets/icon-refreshing.svg';
import companyProfileAts from '../../../strings/companyProfile.ats';
import { getJobsLink } from '../../../helpers/urlHelpers';
import { HireReadyCandidatesBanner } from './HireReadyCandidatesBanner';

export const CompanyProfileLayout = ({ children, fullWidth, showNotPublishedInfo, description }) => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const match = useRouteMatch();
  const company = useSelector(companyDetailsSelector);
  const isCompanyProfile = window.location.pathname.includes('company-profile');
  const isAtsEnabled = !!activeNetwork?.features?.includes('ats_integration');
  const [showHireReadyCandidatesBanner, setShowHireReadyCandidatesBanner] = useLocalStorage(
    `hire-ready-candidates-banner-visible-${activeNetwork?.id}`,
    true,
  );

  const links = [
    {
      label: 'General',
      to: `/networks/${activeNetwork.slug}/${isCompanyProfile ? 'company-profile' : 'companies'}/${
        match.params.companySlug
      }`,
    },
    {
      label: 'Jobs',
      to: getJobsLink({
        networkSlug: activeNetwork.slug,
        companySlug: match.params.companySlug,
        isCompanyProfile,
      }),
    },
    {
      label: 'Job boards',
      to: `/networks/${activeNetwork.slug}/${isCompanyProfile ? 'company-profile' : 'companies'}/${
        match.params.companySlug
      }/job-boards`,
      hide: !(isCompanyProfile && company?.jobBoardCollections?.length > 0),
    },
    {
      label: companyProfileAts.tab,
      to: `/networks/${activeNetwork.slug}/${isCompanyProfile ? 'company-profile' : 'companies'}/${
        match.params.companySlug
      }/ats`,
      hide: !isAtsEnabled,
    },
    {
      label: 'Admins',
      to: `/networks/${activeNetwork.slug}/${isCompanyProfile ? 'company-profile' : 'companies'}/${
        match.params.companySlug
      }/admins`,
      hide: activeNetwork.organization?.id === company?.id,
    },
  ].filter((link) => !link.hide);

  return (
    <>
      <Flex sx={{ flexDirection: 'column', mb: '24px' }}>
        <Title company={company} activeNetwork={activeNetwork} isCompanyProfile={isCompanyProfile} />
        {description && isCompanyProfile && <Text sx={{ fontSize: '14px', px: '16px' }}>{description}</Text>}
        {showNotPublishedInfo && (
          <Flex
            alignItems="center"
            sx={{ border: '1px solid #DEE4ED', borderRadius: '4px', gap: '16px', mt: '16px', mx: '16px' }}
          >
            <Image minWidth="80px" src={Refreshing} />
            <Box>
              <Box fontSize="14px" fontWeight="500" as="h3">
                This company is not published yet
              </Box>
              <Box fontSize="14px" mt="8px" as="p">
                Getro team will add information and publish it, usually it takes 2 business days. Optionally, you can
                edit and publish it. <AddCompanyProcess />
              </Box>
            </Box>
          </Flex>
        )}
        {isCompanyProfile && showHireReadyCandidatesBanner && (
          <HireReadyCandidatesBanner setIsVisible={setShowHireReadyCandidatesBanner} />
        )}
      </Flex>
      <Box sx={{ display: ['undef', 'undef', 'flex'], flex: ['undef', 'undef', 1] }}>
        <PageLayout.Content
          fullWidth={fullWidth}
          contentProps={{ px: '0px' }}
          innerContentProps={{ maxWidth: '100% !important' }}
          contentHeader={
            <Box width={1} as="nav" role="navigation" variant="navigation.subnav" mb="16px" px="16px">
              <Flex as="ul" sx={{ gap: '24px' }}>
                {links.map((link) => (
                  <Box as="li" key={link.label}>
                    <Button
                      as={NavLink}
                      variant="activeUnderline"
                      to={link.to}
                      exact
                      p="0px 8px !important"
                      height="28px"
                      sx={{ fontSize: '14px', lineHeight: '16px' }}
                    >
                      {link.label}
                    </Button>
                  </Box>
                ))}
              </Flex>
            </Box>
          }
        >
          {children}
        </PageLayout.Content>
      </Box>
    </>
  );
};

CompanyProfileLayout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  fullWidth: PropTypes.bool,
  showNotPublishedInfo: PropTypes.bool,
  description: PropTypes.string,
};

CompanyProfileLayout.defaultProps = {
  fullWidth: false,
  showNotPublishedInfo: false,
  description: null,
};
